import { c as _c } from "react/compiler-runtime";
import { Field } from "@/src/components/cards/common";
import { CopyableContent } from "@/src/components/copyable-content";
import type { EhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { properCase } from "@/src/lib/string";
import { getDisplayValue } from "@/src/lib/utils/string-utils";
import { Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
export const PATIENT_HEADER_FIELD_KEYS = new Set(["givenName", "familyName", "dateOfBirth", "gender", "ehrId", "mrn"]);
export type PatientIdentifierResolver = (patient: Patient) => EhrIdentifiers;
export function withResolvedEhrIdFields(fields: Field[], resolveIdentifiers: PatientIdentifierResolver): Field[] {
  const ehrIdFields: Field[] = [{
    key: "ehrId",
    label: "EHR ID",
    comparable: false,
    getValue: (p: Patient) => resolveIdentifiers(p).ehrSystemId
  }, {
    key: "mrn",
    label: "MRN",
    comparable: false,
    getValue: (p: Patient) => resolveIdentifiers(p).mrn
  }];
  return fields.flatMap(field => field.key === "ehrId" ? ehrIdFields : [field]);
}
export function PatientCardTitle(t0) {
  const $ = _c(9);
  const {
    patient
  } = t0;
  let t1;
  if ($[0] !== patient) {
    t1 = getPersonName(patient).trim();
    $[0] = patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  let t2;
  if ($[2] !== patient.middleName) {
    t2 = properCase(patient.middleName);
    $[2] = patient.middleName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const middleName = t2;
  let t3;
  if ($[4] !== middleName || $[5] !== name || $[6] !== patient.familyName || $[7] !== patient.givenName) {
    t3 = <span className="whitespace-normal break-words">{name ? <CopyableContent text={name} className="inline-flex items-center text-lg" textClassName="font-semibold leading-tight">{properCase(patient.givenName)}{middleName && <span className="font-normal text-muted-foreground" title="Middle name">{" "}{middleName}</span>}{" "}{properCase(patient.familyName)}</CopyableContent> : <span className="text-lg font-semibold leading-tight">Name not provided</span>}</span>;
    $[4] = middleName;
    $[5] = name;
    $[6] = patient.familyName;
    $[7] = patient.givenName;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function HeaderItem(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-muted-foreground/70">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = value ? <CopyableContent text={value} /> : <span className="text-muted-foreground/60">—</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span>{t1}{" "}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
export function PatientCardDescription(t0) {
  const $ = _c(15);
  const {
    patient,
    identifiers
  } = t0;
  const {
    ehrSystemId,
    mrn
  } = identifiers;
  let t1;
  if ($[0] !== patient.dateOfBirth) {
    t1 = patient.dateOfBirth ? getDisplayValue(patient.dateOfBirth, "date") : null;
    $[0] = patient.dateOfBirth;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <HeaderItem label="DOB" value={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== patient.gender) {
    t3 = <HeaderItem label="Gender" value={patient.gender} />;
    $[4] = patient.gender;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ehrSystemId) {
    t4 = <HeaderItem label="EHR ID" value={ehrSystemId} />;
    $[6] = ehrSystemId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== mrn) {
    t5 = <HeaderItem label="MRN" value={mrn} />;
    $[8] = mrn;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1">{t2}{t3}{t4}{t5}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}